import { useEffect, useRef, useState } from "react";
import { Check, Circle, MoveUpRight, Pencil, Undo2, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const TOOLS = [
  { key: "cercle", label: "Cercle", icon: Circle },
  { key: "fleche", label: "Flèche", icon: MoveUpRight },
  { key: "libre", label: "Libre", icon: Pencil },
];

export const PhotoAnnotator = ({ src, onCancel, onSave }) => {
  const canvasRef = useRef(null);
  const baseRef = useRef(null);
  const startRef = useRef(null);
  const [tool, setTool] = useState("cercle");
  const [shapes, setShapes] = useState([]);
  const [drawing, setDrawing] = useState(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      baseRef.current = img;
      const canvas = canvasRef.current;
      const maxW = 1000;
      const scale = Math.min(1, maxW / img.width);
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      redraw([], null);
    };
    img.src = src;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const redraw = (list, current) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (baseRef.current) ctx.drawImage(baseRef.current, 0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#FF2D2D";
    ctx.lineWidth = Math.max(3, canvas.width / 220);
    ctx.lineCap = "round";
    [...list, current].filter(Boolean).forEach((shape) => {
      ctx.beginPath();
      if (shape.type === "cercle") {
        const rx = Math.abs(shape.x2 - shape.x1) / 2;
        const ry = Math.abs(shape.y2 - shape.y1) / 2;
        ctx.ellipse((shape.x1 + shape.x2) / 2, (shape.y1 + shape.y2) / 2, rx, ry, 0, 0, Math.PI * 2);
        ctx.stroke();
      } else if (shape.type === "fleche") {
        const { x1, y1, x2, y2 } = shape;
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
        const angle = Math.atan2(y2 - y1, x2 - x1);
        const head = Math.max(12, ctx.lineWidth * 4);
        ctx.beginPath();
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 7), y2 - head * Math.sin(angle - Math.PI / 7));
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 7), y2 - head * Math.sin(angle + Math.PI / 7));
        ctx.stroke();
      } else if (shape.type === "libre" && shape.points?.length > 1) {
        ctx.moveTo(shape.points[0].x, shape.points[0].y);
        shape.points.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
        ctx.stroke();
      }
    });
  };

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return {
      x: ((point.clientX - rect.left) / rect.width) * canvas.width,
      y: ((point.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const down = (e) => {
    e.preventDefault();
    const p = pos(e);
    startRef.current = p;
    setDrawing(tool === "libre" ? { type: "libre", points: [p] } : { type: tool, x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };

  const move = (e) => {
    if (!startRef.current) return;
    e.preventDefault();
    const p = pos(e);
    setDrawing((cur) => {
      if (!cur) return cur;
      const next = cur.type === "libre" ? { ...cur, points: [...cur.points, p] } : { ...cur, x2: p.x, y2: p.y };
      redraw(shapes, next);
      return next;
    });
  };

  const up = () => {
    if (!startRef.current || !drawing) return;
    startRef.current = null;
    const list = [...shapes, drawing];
    setShapes(list);
    setDrawing(null);
    redraw(list, null);
  };

  const undo = () => {
    const list = shapes.slice(0, -1);
    setShapes(list);
    redraw(list, null);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex flex-col p-4" data-testid="photo-annotator">
      <div className="flex items-center justify-between text-white mb-3">
        <div className="text-sm font-semibold">Entourez la zone concernée</div>
        <button data-testid="annotator-close" onClick={onCancel} className="p-2 text-white/60 hover:text-white">
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 min-h-0 grid place-items-center">
        <canvas
          ref={canvasRef}
          data-testid="annotator-canvas"
          className="max-h-full max-w-full rounded-xl touch-none bg-black"
          onMouseDown={down}
          onMouseMove={move}
          onMouseUp={up}
          onMouseLeave={up}
          onTouchStart={down}
          onTouchMove={move}
          onTouchEnd={up}
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {TOOLS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            data-testid={`annotator-tool-${key}`}
            onClick={() => setTool(key)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm border transition-colors ${tool === key ? "bg-[#005CFF] border-[#005CFF] text-white" : "border-white/20 text-white/70 hover:text-white"}`}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
        <button data-testid="annotator-undo" onClick={undo} disabled={!shapes.length} className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm border border-white/20 text-white/70 hover:text-white disabled:opacity-40">
          <Undo2 size={15} /> Annuler
        </button>
        <Button
          data-testid="annotator-save"
          onClick={() => onSave(canvasRef.current.toDataURL("image/jpeg", 0.8))}
          className="ml-auto h-11 rounded-xl bg-[#005CFF] hover:bg-[#0047CC]"
        >
          <Check size={16} className="mr-2" /> Valider l'annotation
        </Button>
      </div>
    </div>
  );
};

export default PhotoAnnotator;
